import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { GTMEvents, GTMLoginEvents, GTMOnboardingEvents, OnboardingSteps } from 'src/app/model/google-tag-manager/api-status';
import { ISelectedContract } from './settings/logged-in-user-settings';
import { UserClaims } from 'src/app/model/okta/userClaims';
import { ADPResourceService } from 'digital-core';
import { Router, ActivatedRoute } from '@angular/router';
import { SelectedDeviceService } from 'src/app/shared/device/selected-device.service';
import { DOCUMENT } from '@angular/common';
import { StorageService } from './storage.service';

// tslint:disable-next-line:ban-types
declare let gtag: Function; // Declare ga as a function

@Injectable({
  providedIn: 'root',
})
export class GoogleAnalyticsService {
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  public selectedContract: ISelectedContract;
  public contractId: string; // Only for onboarding scenario
  public isPrimary: boolean = undefined; // Only for onboarding scenario
  private resourceService = inject(ADPResourceService);
  private selectedDeviceService = inject(SelectedDeviceService);
  private storageService = inject(StorageService);
  private document = inject(DOCUMENT) as Document;


  public emitClickEvent(eventCategory: string, elementName: string, linkURL?: string, linkClasses?: string, claimType?: string, title?: string) {
    const currentUrl = this.router.url;
    const pageTitle = title ?? this.getActiveRouteTitle();
    const location = this.document.location.href;
    const client = this.resourceService.getPortalSetting('Global_ProgramName').Value;

    const dateTime = new Date().toISOString();

    this.emitResetEventData();
    switch (eventCategory) {
      case GTMEvents.Click:
        this.emitGTMEvent({
          event: GTMEvents.Click,
          event_data: {
            page_location: location,
            page_path: currentUrl,
            page_title: pageTitle,
            claim_type: claimType ?? '',
            link_text: elementName,
            link_classes: linkClasses ?? '',
            link_url: linkURL ?? '',
            hit_timestamp: dateTime,
          },
        });
        break;
      case GTMEvents.AccordionExpand:
        this.emitGTMEvent({
          event: GTMEvents.AccordionExpand,
          event_data: {
            page_location: location,
            page_path: currentUrl,
            page_title: pageTitle,
            link_text: elementName, // Article - topic
            link_classes: linkClasses ?? '', // categories
            hit_timestamp: dateTime,
          },
        });
        break;
      case GTMEvents.ChatOpen:
        this.emitResetUserData();
        this.emitGTMEvent({
          event: GTMEvents.ChatOpen,
          event_data: {
            page_location: location,
            page_path: currentUrl,
            page_title: pageTitle,
            link_text: elementName,
            hit_timestamp: dateTime,
            user_data: {
              contract_number: this.selectedContract?.id ?? '',
              user_type: this.selectedContract?.isPrimary === undefined ? '' : this.selectedContract.isPrimary ? 'Primary' : 'Secondary',
            },
          },
        });
        break;
    }
  }

  public emitErrorEvent(message: string, type: string, code: string) {
    const currentUrl = this.router.url;
    const pageTitle = this.getActiveRouteTitle();
    const location = this.document.location.href;

    const dateTime = new Date().toISOString();

    this.emitResetUserData();
    this.emitResetEventData();
    this.emitGTMEvent({
      event: GTMEvents.Error,
      event_data: {
        page_location: location,
        page_path: currentUrl,
        page_title: pageTitle,
        error_message: message,
        error_type: type,
        error_code: code,
        hit_timestamp: dateTime,
        user_data: {
          contract_number: this.selectedContract?.id ?? '',
          user_type: this.selectedContract?.isPrimary === undefined ? '' : this.selectedContract.isPrimary ? 'Primary' : 'Secondary',
        },
      },
    });
  }

  public emitGTMEvent(data: any) {
    gtag(data);
  }

  public getErrorCodeForGTM(error: any) {
    if (error instanceof HttpErrorResponse) {
      let errorResponse: any;
      if (error.error && error.error.error) {
        errorResponse = error.error.error;
      } else {
        errorResponse = error;
      }
      if (errorResponse && errorResponse.hasOwnProperty('httpStatusCode')) {
        return errorResponse.code;
      } else {
        return `${error.status}: ${error.statusText}`;
      }
    }
    return 'ApplicationError';
  }

  public performanceEmitter(eventCategory: string, eventName: string, startTime: number) {
    const totalTime = Date.now() - startTime;

    gtag('event', 'timing_complete', {
      name: eventName,
      value: totalTime,
      event_category: eventCategory,
    });
  }

  public apiPerformanceEmitter(eventName: string, startTime: number) {
    const totalTime = Date.now() - startTime;
    this.emitGTMEvent({
      event: 'timing_complete',
      name: eventName,
      value: totalTime,
      event_category: 'api_timing',
      event_label: 'api',
    });
  }

  public isUserPrimary(contract: ISelectedContract, userClaims: UserClaims) {
    let check = false;
    const isEprismOnly = this.storageService.getLocal<string>(`IsEprismOnly`);
    if (isEprismOnly == 'true') {
      return true; // Eprism contract only is primary user
    }
    const loggedInMember = contract?.members?.find((m) => m?.emailAddress?.toLowerCase() === userClaims?.email?.toLowerCase());

    if (loggedInMember) {
      check = loggedInMember?.emailAddress?.toLowerCase() === contract?.primaryUserEmail?.toLowerCase();
    }

    return check;
  }

  public hashCode(s: string) {
    let hash = 0;
    if (s.length === 0) return hash;

    for (let i = 0; i < s.length; i++) {
      const charCode = s.charCodeAt(i);
      hash = (hash << 7) - hash + charCode;
      hash = hash & hash;
    }
    return hash;
  }

  public emitPageViewEvent() {
    const currentUrl = this.router.url;
    const pageTitle = this.getActiveRouteTitle();
    const location = this.document.location.href;
    const devicesCount = this.selectedDeviceService.getAllDevices()?.length ?? 0;
    const client = this.resourceService.getPortalSetting('Global_ProgramName').Value;

    this.emitResetUserData();
    this.emitGTMEvent({
      event: GTMEvents.PageView,
      page_data: {
        page_location: location,
        page_path: currentUrl,
        client: client.toString(),
        device_count: devicesCount,
        page_title: pageTitle,
      },
      user_data: {
        contract_number: this.selectedContract?.id ?? '',
        user_type: this.selectedContract?.isPrimary === undefined ? '' : this.selectedContract.isPrimary ? 'Primary' : 'Secondary',
      },
    });
  }

  public emitDeviceInventoryEvent(event: string, flowType: string, deviceType: string, deviceMake: string, deviceModel: string) {
    const currentUrl = this.router.url;
    const pageTitle = this.getActiveRouteTitle();
    const location = this.document.location.href;
    const devicesCount = this.selectedDeviceService.getAllDevices()?.length ?? 0;
    const dateTime = new Date().toISOString();

    this.emitResetUserData();
    this.emitResetEventData();
    switch (event) {
      case GTMEvents.DeviceInventoryView:
        this.emitGTMEvent({
          event: GTMEvents.DeviceInventoryView,
          event_data: {
            device_count: devicesCount,
            hit_timestamp: dateTime,
          },
          user_data: {
            contract_number: this.selectedContract?.id ?? '',
            user_type: this.selectedContract?.isPrimary === undefined ? '' : this.selectedContract.isPrimary ? 'Primary' : 'Secondary',
          },
        });
        break;
      case GTMEvents.DeviceInventoryAddView:
        this.emitGTMEvent({
          event: GTMEvents.DeviceInventoryAddView,
          event_data: {
            page_location: location,
            page_title: pageTitle,
            page_path: currentUrl,
            flow_type: flowType,
            device_type: deviceType,
            device_make: deviceMake,
            device_model: deviceModel,
            hit_timestamp: dateTime,
          },
          user_data: {
            contract_number: this.selectedContract?.id ?? '',
            user_type: this.selectedContract?.isPrimary === undefined ? '' : this.selectedContract.isPrimary ? 'Primary' : 'Secondary',
          },
        });
        break;
      case GTMEvents.DeviceInventoryAddFail:
        this.emitGTMEvent({
          event: GTMEvents.DeviceInventoryAddFail,
          event_data: {
            page_location: location,
            page_title: pageTitle,
            page_path: currentUrl,
            flow_type: flowType,
            total_devices: devicesCount,
            device_type: deviceType,
            device_make: deviceMake,
            device_model: deviceModel,
            hit_timestamp: dateTime,
          },
          user_data: {
            contract_number: this.selectedContract?.id ?? '',
            user_type: this.selectedContract?.isPrimary === undefined ? '' : this.selectedContract.isPrimary ? 'Primary' : 'Secondary',
          },
        });
        break;
      case GTMEvents.DeviceInventoryAddComplete:
        this.emitGTMEvent({
          event: GTMEvents.DeviceInventoryAddComplete,
          event_data: {
            page_location: location,
            page_title: pageTitle,
            page_path: currentUrl,
            flow_type: flowType,
            total_devices: devicesCount,
            device_type: deviceType,
            device_make: deviceMake,
            device_model: deviceModel,
            hit_timestamp: dateTime,
          },
          user_data: {
            contract_number: this.selectedContract?.id ?? '',
            user_type: this.selectedContract?.isPrimary === undefined ? '' : this.selectedContract.isPrimary ? 'Primary' : 'Secondary',
          },
        });
        break;
    }
  }

  public emitOnboardingEvent(event: GTMOnboardingEvents, step: OnboardingSteps, error: any = undefined) {
    const currentUrl = this.router.url;
    const pageTitle = this.getActiveRouteTitle();
    const location = this.document.location.href;
    const dateTime = new Date().toISOString();

    this.emitResetEventData();
    switch (event) {
      case GTMOnboardingEvents.Start:
        this.emitGTMEvent({
          event: GTMOnboardingEvents.Start,
          event_data: {
            step_name: step.toString(),
            page_title: pageTitle,
            page_path: currentUrl,
            page_location: location,
            hit_timestamp: dateTime,
          },
        });
        break;
      case GTMOnboardingEvents.InProcess:
        this.emitResetUserData();
        this.emitGTMEvent({
          event: GTMOnboardingEvents.InProcess,
          event_data: {
            step_name: step.toString(),
            page_title: pageTitle,
            page_path: currentUrl,
            page_location: location,
            hit_timestamp: dateTime,
          },
          user_data: {
            contract_number: this.contractId ?? '',
            user_type: this.isPrimary === undefined ? '' : this.isPrimary ? 'Primary' : 'Secondary',
          },
        });
        break;
      case GTMOnboardingEvents.Complete:
        this.emitResetUserData();
        this.emitGTMEvent({
          event: GTMOnboardingEvents.Complete,
          event_data: {
            step_name: step.toString(),
            page_title: pageTitle,
            page_path: currentUrl,
            page_location: location,
            hit_timestamp: dateTime,
          },
          user_data: {
            contract_number: this.contractId ?? '',
            user_type: this.isPrimary === undefined ? '' : this.isPrimary ? 'Primary' : 'Secondary',
          },
        });
        break;
      case GTMOnboardingEvents.Failed:
        this.emitGTMEvent({
          event: GTMOnboardingEvents.Failed,
          event_data: {
            step_name: step.toString(),
            page_title: pageTitle,
            page_path: currentUrl,
            page_location: location,
            hit_timestamp: dateTime,
            error_message: error?.message,
            error_type: error?.type,
            error_code: error?.code
          },
        });
        break;
      default:
        break;
    }
  }

  public emitLoginEvent(event: GTMLoginEvents, error: any = undefined) {
    const currentUrl = this.router.url;
    const pageTitle = this.getActiveRouteTitle();
    const location = this.document.location.href;
    const dateTime = new Date().toISOString();


    this.emitResetUserData();
    this.emitResetEventData();
    switch (event) {
      case GTMLoginEvents.Login:
        this.emitGTMEvent({
          event: GTMLoginEvents.Login,
          event_data: {
            page_title: pageTitle,
            page_path: currentUrl,
            page_location: location,
            hit_timestamp: dateTime,
          },
        });
        break;
      case GTMLoginEvents.LoginSuccess:
        this.emitGTMEvent({
          event: GTMLoginEvents.LoginSuccess,
          event_data: {
            page_title: pageTitle,
            page_path: currentUrl,
            page_location: location,
            hit_timestamp: dateTime,
          },
          user_data: {
            contract_number: this.selectedContract?.id ?? '',
            user_type: this.selectedContract?.isPrimary === undefined ? '' : this.selectedContract.isPrimary ? 'Primary' : 'Secondary',
          },
        });
        break;
      case GTMLoginEvents.LoginError:
        this.emitGTMEvent({
          event: GTMLoginEvents.LoginError,
          event_data: {
            page_title: pageTitle,
            page_path: currentUrl,
            page_location: location,
            hit_timestamp: dateTime,
            error_message: error?.message,
            error_type: error?.type,
            error_code: error?.code
          },
          user_data: {
            contract_number: this.selectedContract?.id ?? '',
            user_type: this.selectedContract?.isPrimary === undefined ? '' : this.selectedContract.isPrimary ? 'Primary' : 'Secondary',
          },
        });
        break;
      default:
        break;
    }
  }

  public getActiveRouteTitle(): string {
    let title = '';
    let route = this.activatedRoute;

    while (route) {
      if (route.snapshot.data && route.snapshot.data.title) {
        title = route.snapshot.data.title;
      }

      if (route.firstChild) {
        route = route.firstChild;
      } else {
        break;
      }
    }

    return title;
  }

  public setContractData(contract: ISelectedContract) {
    this.selectedContract = contract;
  }

  emitResetUserData(){
    this.emitGTMEvent({
      user_data: null
    });
  }

  emitResetEventData(){
    this.emitGTMEvent({
      event_data: null
    });
  }


}
